import RefreshIcon from "@mui/icons-material/Refresh";
import {
	Box,
	Grid2,
	IconButton,
	Paper,
	Tooltip,
	Typography,
} from "@mui/material";
import * as React from "react";
import { useQueryCacheStats } from "../../hooks/useQueryCacheStats";

const formatBytes = (bytes: number) => {
	if (!bytes) return "0 B";
	const units = ["B", "KB", "MB", "GB"];
	const exponent = Math.min(
		Math.floor(Math.log(bytes) / Math.log(1024)),
		units.length - 1,
	);
	const value = bytes / 1024 ** exponent;
	return `${value.toFixed(value >= 100 || exponent === 0 ? 0 : 1)} ${units[exponent]}`;
};

const Figure = ({
	label,
	value,
	hint,
}: {
	label: string;
	value: string;
	hint?: string;
}) => (
	<Box>
		<Typography variant="caption" color="text.secondary" display="block">
			{hint ? (
				<Tooltip title={hint}>
					<span style={{ borderBottom: "1px dotted #90a4ae" }}>{label}</span>
				</Tooltip>
			) : (
				label
			)}
		</Typography>
		<Typography variant="h6" fontWeight={600} lineHeight={1.2}>
			{value}
		</Typography>
	</Box>
);

// Memory the cache is holding right now, so it is visible whether keeping the catalogues in the
// API process is still reasonable. Cheap to call: the sizes are measured once when each entry is
// stored, this only adds them up.
export const QueryCacheUsage = () => {
	const { data, isLoading, refetch, isFetching, dataUpdatedAt } =
		useQueryCacheStats();

	// These figures are a snapshot, not a live feed, and without the timestamp there is no way to
	// tell whether what you are looking at is from a second ago or from before you reloaded a page.
	const readAt = dataUpdatedAt
		? new Date(dataUpdatedAt).toLocaleTimeString()
		: null;

	return (
		<Paper sx={{ p: 1.5, width: "100%" }}>
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					mb: 1,
				}}
			>
				<Typography variant="subtitle2" fontWeight={700}>
					Memory in use
					{data?.store ? (
						<Typography
							component="span"
							variant="caption"
							color="text.secondary"
							sx={{ ml: 1 }}
						>
							stored in{" "}
							{data.store === "memory" ? "the API process" : data.store}
						</Typography>
					) : null}
				</Typography>
				<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
					{readAt ? (
						<Typography variant="caption" color="text.secondary">
							{isFetching ? "reading..." : `read at ${readAt}`}
						</Typography>
					) : null}
					<Tooltip title="Read again now">
						<span>
							<IconButton
								size="small"
								onClick={() => refetch()}
								disabled={isFetching}
							>
								<RefreshIcon fontSize="small" />
							</IconButton>
						</span>
					</Tooltip>
				</Box>
			</Box>

			{isLoading ? (
				<Typography variant="body2" color="text.secondary">
					Reading...
				</Typography>
			) : !data ? (
				<Typography variant="body2" color="text.secondary">
					Usage could not be read.
				</Typography>
			) : !data.measurable ? (
				<Typography variant="body2" color="text.secondary">
					This store cannot report what it holds, so there is nothing to show
					here. Ask the {data.store} server itself.
				</Typography>
			) : (
				<>
					<Grid2 container spacing={3}>
						<Grid2>
							<Figure
								label="Cached data"
								value={formatBytes(data.dataBytes)}
								hint="Size of the cached rows written out as text. The same rows held as objects take up more, so read this as the floor and the process memory as the ceiling."
							/>
						</Grid2>
						<Grid2>
							<Figure
								label="Rows"
								value={`${data.rows.toLocaleString()}${
									data.rowsLimit ? ` / ${data.rowsLimit.toLocaleString()}` : ""
								}`}
								hint="Rows held now against the ceiling the cache enforces. Past the ceiling the oldest entries are dropped to make room for the new one, so nothing breaks: the cache just holds less."
							/>
						</Grid2>
						<Grid2>
							<Figure
								label="Entries"
								value={data.entries.toLocaleString()}
								hint="One entry per combination of parameters, and per user on procedures that take @User."
							/>
						</Grid2>
						<Grid2>
							<Figure
								label="API process"
								value={formatBytes(data.processManagedBytes)}
								hint="Memory of the whole API, cache included. The ceiling the cached data sits under."
							/>
						</Grid2>
					</Grid2>

					{data.actions?.length ? (
						<Box sx={{ mt: 1.5 }}>
							{data.actions.map((a) => (
								<Box
									key={a.actionName}
									sx={{
										display: "flex",
										justifyContent: "space-between",
										gap: 2,
										borderTop: "1px solid #eceff1",
										py: 0.4,
									}}
								>
									<Typography variant="caption">{a.actionName}</Typography>
									<Typography variant="caption" color="text.secondary">
										{a.rows.toLocaleString()} rows · {formatBytes(a.dataBytes)}
									</Typography>
								</Box>
							))}
						</Box>
					) : (
						<Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
							Nothing cached yet. It fills up as the configured actions are
							called.
						</Typography>
					)}
				</>
			)}
		</Paper>
	);
};
